import React from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { Message } from 'semantic-ui-react'
import styled from 'styled-components'
import { formatNumber } from '~/utils/helpers'

const StyledMessage = styled(Message) <{ $withoutPercentage?: boolean }>`
background-color: #fcfff5 !important;
color: #2c662d !important;
padding: ${props => props.$withoutPercentage ? '1.5em' : '0.5em 1em'} !important;
width: 450px;
${props => props.$withoutPercentage && 'height: 132px !important;'}
`

interface Props {
  withPercentage?: boolean
  silageKey?: 'grass' | 'maize'
}

const Total: React.FC<Props> = ({ withPercentage, silageKey }) => {
  const { control } = useFormContext()
  const watchOrganicManures = useWatch({ control, name: `${silageKey}Silage.organicFertilizers` }) || []
  const watchMineralManures = useWatch({ control, name: `${silageKey}Silage.mineralFertilizers` }) || []
  const watchManures = [...watchOrganicManures, ...watchMineralManures]

  const sumNitro = watchManures.reduce((total, item) => {
    const numericValue = parseFloat(item?.value?.replace(',', '.'));
    const nitroValue = parseFloat(item?.n?.replace(',', '.'));
    const nitroTransformed = isNaN(nitroValue) ? 0 : nitroValue
    const percentage = withPercentage ? parseInt(item.nUsable) / 100 : 1
    return total + (isNaN(numericValue) ? 0 : numericValue * nitroTransformed) * percentage;
  }, 0)

  const sumPho = watchManures.reduce((total, item) => {
    const numericValue = parseFloat(item?.value?.replace(',', '.'));
    const phoValue = parseFloat(item?.p2o5?.replace(',', '.'));
    const phoTransformed = isNaN(phoValue) ? 0 : phoValue
    const percentage = withPercentage ? parseInt(item.p2o5Usable) / 100 : 1
    return total + (isNaN(numericValue) ? 0 : numericValue * phoTransformed) * percentage;
  }, 0)

  const sumKa = watchManures.reduce((total, item) => {
    const numericValue = parseFloat(item?.value?.replace(',', '.'));
    const kaliumValue = parseFloat(item?.k2o?.replace(',', '.'));
    const kaliumTransformed = isNaN(kaliumValue) ? 0 : kaliumValue
    const percentage = withPercentage ? parseInt(item.k2oUsable) / 100 : 1
    return total + (isNaN(numericValue) ? 0 : numericValue * kaliumTransformed) * percentage;
  }, 0)

  const header = withPercentage ?
    'Die ausgebrachte, nutzbare Menge beträgt:' :
    'Der Nährstoffbedarf gesamt nach Nährstoffabfuhr beträgt:'

  if (withPercentage) {
    return <div>
      <StyledMessage>
        <Message.Header>{`Die ausgebrachte, nutzbare N-Menge beträgt: ${formatNumber(sumNitro, 2)} kg N/ha`}</Message.Header>
      </StyledMessage>
      <StyledMessage>
        <Message.Header>{`Ihre Menge an Phosphor-Mineraldünger beträgt: ${formatNumber(sumPho, 2)} kg P₂O₅/ha`}</Message.Header>
      </StyledMessage>
      <StyledMessage>
        <Message.Header>{`Ihre Menge an Kalium-Mineraldünger beträgt: ${formatNumber(sumKa, 2)} kg K₂O/ha`}</Message.Header>
      </StyledMessage>
    </div>
  }

  return <StyledMessage $withoutPercentage={!withPercentage}>
    <Message.Header>{header}</Message.Header>
    <Message.Content>
      <div>{`${formatNumber(sumNitro, 2)} kg N/ha`}</div>
      <div dangerouslySetInnerHTML={{ __html: `${formatNumber(sumPho, 2)} kg <span>P<sub>2</sub>O<sub>5</sub>/ha</span>` }} />
      <div dangerouslySetInnerHTML={{ __html: `${formatNumber(sumKa, 2)} kg <span>K<sub>2</sub>O/ha</span>` }} />
    </Message.Content>
  </StyledMessage>
}
export default Total